import { DateOnly } from '@if/core/date'
import { EOriginSystemId, EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import {
  ControlledCheckbox,
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  createLoadingStore,
  FieldPrerequisiteSpec,
  FieldReadyGate,
  FieldReadyGateProvider,
  renderSelectMenuItems,
  useAsyncValidation,
  useFormContainerContext,
  useReconcileFieldToOptions,
  useReportFieldLoading,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Alert, Checkbox, FormControlLabel, Grid, MenuItem, Skeleton, Tooltip } from '@mui/material'
import {
  FirstPaymentDateOptionsDto,
  useGetFirstPaymentDateOptions,
} from '@src/data/api/credit-application/credit-application-api'
import { useValidTerms } from '@src/data/hooks/terms-hooks'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { CustomPromoPlanOption } from '@src/data/types/PromoPlan'
import { formatPaymentPlanForList } from '@src/services/Formatter'
import { memo, ReactElement, ReactNode, useEffect, useMemo, useRef } from 'react'
import { Control, FieldPath, FieldPathByValue, FieldValues, useController, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  BaseWorksheetFormConfig,
  BaseWorksheetFormConfigProvider,
  findMissingFinancingConfigKeys,
  useBaseWorksheetFormConfig,
} from './BaseWorksheetForm.config'
import { getMaxPmtAmountWithAgentLeeway, isPromoPlan } from './BaseWorksheetForm.helpers'
type TranslateFn = (key: string) => string
const cellSize = { xs: 12, md: 4 } as const
const SHORT_TERM_CAP = 72
const PROMO_ONLY_TERM = 6

const FIELD_READY_GATE_CONFIG = {
  fillPrerequisitesFirstKey: 'worksheetCommon.fillPrerequisitesFirst',
  waitingForPrerequisitesKey: 'worksheetCommon.waitingForPrerequisites',
} as const

const FIRST_PAYMENT_DATE_PICKER_THRESHOLD = 12

const translateInvalidTermReason = (t: TranslateFn, reason: string | undefined): string => {
  switch (reason) {
    case 'pmtTooHigh':
      return t('worksheetCommon.errors.pmtTooHigh')
    default:
      return reason ?? ''
  }
}

interface BaseWorksheetFormProps {
  config: BaseWorksheetFormConfig
  children: ReactNode
}

const BaseWorksheetForm = ({ config, children }: BaseWorksheetFormProps) => {
  const { t } = useTranslation()
  const { setBusy } = useFormContainerContext()
  const loadingStore = useMemo(() => createLoadingStore(), [])

  const setBusyRef = useRef(setBusy)
  setBusyRef.current = setBusy
  useEffect(() => {
    let last = loadingStore.isAnyLoading()
    setBusyRef.current('worksheetFields', last)
    const unsubscribe = loadingStore.subscribe(() => {
      const next = loadingStore.isAnyLoading()
      if (next === last) return
      last = next
      setBusyRef.current('worksheetFields', next)
    })
    return () => {
      unsubscribe()
      setBusyRef.current('worksheetFields', false)
    }
  }, [loadingStore])

  const missing = useMemo(() => findMissingFinancingConfigKeys(config), [config])
  const isConfigValid = missing.length === 0
  useEffect(() => {
    setBusy('configInvalid', !isConfigValid)
    return () => setBusy('configInvalid', false)
  }, [setBusy, isConfigValid])

  return (
    <BaseWorksheetFormConfigProvider value={config}>
      <FieldReadyGateProvider store={loadingStore} config={FIELD_READY_GATE_CONFIG}>
        {!isConfigValid && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('worksheetCommon.errors.configInvalid', { missing: missing.join(', ') })}
          </Alert>
        )}
        <Grid container spacing={2.5}>
          {children}
        </Grid>
      </FieldReadyGateProvider>
    </BaseWorksheetFormConfigProvider>
  )
}

interface AmountRequestedProps<T extends FieldValues & { amountRequested: number }> {
  control: Control<T>
  label?: string
  disabled?: boolean
}

const AmountRequested = <T extends FieldValues & { amountRequested: number }>({
  control,
  label,
  disabled,
}: AmountRequestedProps<T>) => {
  const { t } = useTranslation()
  return (
    <Grid size={cellSize}>
      <ControlledNumberField
        name={'amountRequested' as FieldPathByValue<T, number>}
        control={control}
        label={label ?? t('worksheetCommon.amountRequested')}
        isMoney
        disabled={disabled}
        fullWidth
      />
    </Grid>
  )
}

interface PaymentPlanProps<
  T extends FieldValues & { paymentPlanId: string; term: number | null },
> {
  control: Control<T>
  label?: string
}

// Custom-PromoPlan merchant: single opt-in toggle instead of the legacy plan select. On carries the
// promoPlan's PromoPlan.Id directly in paymentPlanId; the backend resolves it (current or superseded revision).
const CustomPromoPlanToggle = <
  T extends FieldValues & { paymentPlanId: string; term: number | null },
>({
  control,
  label,
  promoPlan,
}: PaymentPlanProps<T> & { promoPlan: CustomPromoPlanOption | null }) => {
  const { t } = useTranslation()
  const { field: planField } = useController({ control, name: 'paymentPlanId' as FieldPathByValue<T, string> })
  const { field: termField } = useController({ control, name: 'term' as FieldPathByValue<T, number> })

  const hasActivePromoPlan = !!promoPlan && promoPlan.termRates.length > 0
  const offeredTerms = useMemo(() => promoPlan?.termRates.map((r) => r.termMonths) ?? [], [promoPlan])
  // Presence-based: any non-Regular plan on a promoPlan merchant is a promoPlan GUID (current or superseded), so a
  // stored revision still reads ON even when it differs from the currently-active promoPlan.id.
  const isOn = planField.value !== (EPaymentPlan.RegularDailyInterests as string)

  const handleToggle = (checked: boolean) => {
    if (checked) {
      if (!promoPlan) return
      planField.onChange(promoPlan.id)
      // Snap the term into the promoPlan's offered set so a stale regular-path term can't leak through.
      if (termField.value == null || !offeredTerms.includes(termField.value)) {
        termField.onChange(offeredTerms[0] ?? null)
      }
    } else {
      // OFF must run even when no promoPlan is currently active, so a preserved stored GUID can be cleared.
      planField.onChange(EPaymentPlan.RegularDailyInterests)
      termField.onChange(null)
    }
  }

  const tooltipTitle = hasActivePromoPlan
    ? (promoPlan?.label ?? '')
    : t('worksheetCommon.customPromoPlanUnavailable')

  // A preserved stored promoPlan (isOn) stays toggle-off-able; it just can't be turned on when none is offered.
  const disabled = !hasActivePromoPlan && !isOn

  // span wrapper so the Tooltip still fires while the checkbox is disabled.
  return (
    <Grid size={12}>
      <Tooltip title={tooltipTitle}>
        <span>
          <FormControlLabel
            control={
              <Checkbox checked={isOn} disabled={disabled} onChange={(e) => handleToggle(e.target.checked)} />
            }
            label={label ?? t('worksheetCommon.customPromoPlan')}
          />
        </span>
      </Tooltip>
    </Grid>
  )
}

const LegacyPaymentPlanSelect = <
  T extends FieldValues & { paymentPlanId: string; term: number | null },
>({
  control,
  label,
}: PaymentPlanProps<T>) => {
  const { t } = useTranslation()
  const { plans, availablePaymentPlans } = useBaseWorksheetFormConfig()

  const paymentPlanIdName = 'paymentPlanId' as FieldPathByValue<T, string>
  const currentPaymentPlanId = useWatch({ control, name: paymentPlanIdName }) as string | undefined

  const options = useMemo(() => {
    const items: ReactElement[] = [
      <MenuItem key={EPaymentPlan.RegularDailyInterests} value={EPaymentPlan.RegularDailyInterests}>
        {t('EPaymentPlan.regularInterest', { ns: 'enum' })}
      </MenuItem>,
    ]

    const seenIds = new Set<string>([EPaymentPlan.RegularDailyInterests])
    for (const plan of availablePaymentPlans) {
      if (seenIds.has(plan.id as string)) continue
      seenIds.add(plan.id as string)
      items.push(
        <MenuItem key={plan.id} value={plan.id}>
          {formatPaymentPlanForList(plan)}
        </MenuItem>,
      )
    }

    if (currentPaymentPlanId && !seenIds.has(currentPaymentPlanId)) {
      const fallback = plans.find((p) => (p.id as string) === currentPaymentPlanId)
      if (fallback) {
        items.push(
          <MenuItem key={fallback.id} value={fallback.id} disabled>
            {formatPaymentPlanForList(fallback)}
          </MenuItem>,
        )
      }
    }

    return items
  }, [t, plans, availablePaymentPlans, currentPaymentPlanId])

  const selectedPlan = useMemo(() => plans.find((p) => p.id === currentPaymentPlanId), [plans, currentPaymentPlanId])
  const onPromoPlan = isPromoPlan(currentPaymentPlanId)
  const { field: termField } = useController({
    control,
    name: 'term' as FieldPathByValue<T, number>,
  })
  useEffect(() => {
    if (!onPromoPlan || !selectedPlan) return
    if (termField.value !== selectedPlan.loanTerm) {
      termField.onChange(selectedPlan.loanTerm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPromoPlan, selectedPlan?.loanTerm])

  return (
    <Grid size={cellSize}>
      <ControlledSelect
        name={paymentPlanIdName}
        control={control}
        label={label ?? t('worksheetCommon.paymentPlan')}
        options={options}
        disableEmptyOption
        fullWidth
      />
    </Grid>
  )
}

const PaymentPlan = <
  T extends FieldValues & { paymentPlanId: string; term: number | null },
>({
  control,
  label,
}: PaymentPlanProps<T>) => {
  const { isCustomPromoPlanMerchant, customPromoPlan, isPaymentPlanLoading } = useBaseWorksheetFormConfig()
  // Skeleton until the feature switch settles, rather than flashing the wrong control.
  if (isPaymentPlanLoading) {
    return (
      <Grid size={cellSize}>
        <Skeleton variant="rounded" height={56} data-testid="payment-plan-loading" />
      </Grid>
    )
  }
  return isCustomPromoPlanMerchant ? (
    <CustomPromoPlanToggle control={control} label={label} promoPlan={customPromoPlan} />
  ) : (
    <LegacyPaymentPlanSelect control={control} label={label} />
  )
}

type TermConstraint = FieldValues & {
  term: number | null
  paymentPlanId: string
  paymentFrequency: EPaymentFrequency
  activationDate: DateOnly
  firstPaymentDate: DateOnly | null
  amountRequested: number
  includeProtection: boolean
}

const TERM_PREREQUISITES = [
  { name: 'paymentPlanId', prefixKey: 'worksheetCommon', labelKey: 'paymentPlan' },
  { name: 'paymentFrequency', prefixKey: 'worksheetCommon' },
  { name: 'activationDate', prefixKey: 'worksheetCommon' },
  { name: 'firstPaymentDate', prefixKey: 'worksheetCommon' },
  { name: 'amountRequested', prefixKey: 'worksheetCommon' },
] as const satisfies readonly FieldPrerequisiteSpec<TermConstraint, 'worksheetCommon'>[]

const TERM_WATCH = ['includeProtection', 'term'] as const

interface TermProps<T extends TermConstraint> {
  control: Control<T>
  label?: string
  disabled?: boolean
  maxTerm?: number
}

const Term = <T extends TermConstraint>({ control, label, disabled, maxTerm }: TermProps<T>) => {
  const { t } = useTranslation()
  const fieldLabel = label ?? t('worksheetCommon.term')
  const termName = 'term' as FieldPathByValue<T, number>

  return (
    <Grid size={cellSize}>
      <FieldReadyGate
        control={control}
        requiredFields={TERM_PREREQUISITES}
        watch={TERM_WATCH}
        selfName="term"
        shell={
          <ControlledSelect
            name={termName}
            control={control}
            label={fieldLabel}
            options={[]}
            parseValue={(v) => (v !== null && v !== '' ? Number(v) : null)}
            disabled
            disableEmptyOption
            fullWidth
          />
        }
      >
        {(values) => (
          <TermActive
            control={control}
            fieldLabel={fieldLabel}
            disabled={!!disabled}
            maxTerm={maxTerm}
            paymentPlanId={values.paymentPlanId}
            paymentFrequency={values.paymentFrequency}
            activationDate={values.activationDate}
            firstPaymentDate={values.firstPaymentDate!}
            amountRequested={values.amountRequested}
            includeProtection={values.includeProtection}
          />
        )}
      </FieldReadyGate>
    </Grid>
  )
}

interface TermActiveProps<T extends TermConstraint> {
  control: Control<T>
  fieldLabel: string
  disabled: boolean
  maxTerm?: number
  paymentPlanId: string
  paymentFrequency: EPaymentFrequency
  activationDate: DateOnly
  firstPaymentDate: DateOnly
  amountRequested: number
  includeProtection: boolean
}

const TermActiveImpl = <T extends TermConstraint>({
  control,
  fieldLabel,
  disabled,
  maxTerm,
  paymentPlanId,
  paymentFrequency,
  activationDate,
  firstPaymentDate,
  amountRequested,
  includeProtection,
}: TermActiveProps<T>) => {
  const { t } = useTranslation()
  const {
    supportedTerms,
    minLoanTerm,
    maxLoanTerm,
    largeLoanAmount,
    longTerm,
    creditApplicationId,
    financingProgramId,
    interestRate,
    hasCoApplicant,
    stateIso,
    maxPmtAmount: rawMaxPmtAmount,
    merchantId,
    maxTermDuration: decisionMaxTerm,
    useAgentLeeway,
    filteredTermsByMaxPayment,
    customPromoPlan,
  } = useBaseWorksheetFormConfig()

  const maxPmtAmount = useMemo(
    () =>
      rawMaxPmtAmount == null
        ? undefined
        : useAgentLeeway
          ? getMaxPmtAmountWithAgentLeeway(paymentPlanId, rawMaxPmtAmount)
          : rawMaxPmtAmount,
    [useAgentLeeway, paymentPlanId, rawMaxPmtAmount],
  )

  // Presence-based: an active promoPlan plus a non-Regular paymentPlanId means the promoPlan drives terms.
  const promoPlanActive = !!customPromoPlan && isPromoPlan(paymentPlanId)

  const allowedTerms = useMemo(() => {
    const programMin = minLoanTerm > 0 ? minLoanTerm : 0
    const onPromoPlan = isPromoPlan(paymentPlanId)
    const baseTerms: number[] = promoPlanActive
      ? customPromoPlan.termRates.map((r) => r.termMonths)
      : supportedTerms.map(Number)
    const caps: number[] = []
    if (maxLoanTerm > 0) caps.push(maxLoanTerm)
    if (decisionMaxTerm) caps.push(decisionMaxTerm)
    if (maxTerm && maxTerm > 0) caps.push(maxTerm)
    if (largeLoanAmount > 0 && longTerm > 0) {
      caps.push((amountRequested ?? 0) >= largeLoanAmount ? longTerm : SHORT_TERM_CAP)
    }
    return baseTerms.filter((value) => {
      if (value < programMin) return false
      // 6-month term is promo-only.
      if (value === PROMO_ONLY_TERM && !onPromoPlan) return false
      return caps.every((cap) => value <= cap)
    })
  }, [
    supportedTerms,
    promoPlanActive,
    customPromoPlan,
    minLoanTerm,
    maxLoanTerm,
    largeLoanAmount,
    longTerm,
    decisionMaxTerm,
    maxTerm,
    amountRequested,
    paymentPlanId,
  ])

  // Legacy promo plans pin a single term; the promoPlan offers several, so it stays user-selectable.
  const isLockedByPromoPlan = isPromoPlan(paymentPlanId) && !promoPlanActive

  const validTermsArgs = useMemo(() => {
    if (!filteredTermsByMaxPayment || maxPmtAmount == null) return null
    if (!paymentPlanId || !paymentFrequency || !activationDate || !firstPaymentDate || !amountRequested) {
      return null
    }
    return {
      creditApplicationId,
      financingProgramId,
      interestRate,
      hasCoApplicant,
      stateIso,
      maxPmtAmount,
      merchantId,
      paymentFrequency,
      paymentPlanId,
      deliveryOn: activationDate,
      firstPaymentOn: firstPaymentDate.toDate(),
      amountRequested,
      includeInsurance: includeProtection ?? false,
      possibleTerms: allowedTerms,
    }
  }, [
    filteredTermsByMaxPayment,
    creditApplicationId,
    financingProgramId,
    interestRate,
    hasCoApplicant,
    stateIso,
    maxPmtAmount,
    merchantId,
    paymentFrequency,
    paymentPlanId,
    activationDate,
    firstPaymentDate,
    amountRequested,
    includeProtection,
    allowedTerms,
  ])

  const [validTerms, invalidTerms, isLoadingTerms] = useValidTerms(validTermsArgs)

  const applyValidation = !isLoadingTerms && (validTerms.size > 0 || invalidTerms.size > 0)

  const termData = useMemo(
    () => (applyValidation ? { validTerms, maxPmtAmount } : null),
    [applyValidation, validTerms, maxPmtAmount],
  )
  useAsyncValidation('term', { loading: isLoadingTerms, data: termData })

  const options = useMemo(
    () =>
      renderSelectMenuItems(allowedTerms, (term) => {
        const isInvalid = applyValidation && !validTerms.has(term)
        const reasonKey = isInvalid ? invalidTerms.get(term) : undefined
        return {
          value: term,
          label: term,
          disabled: isInvalid,
          tooltip: translateInvalidTermReason(t as TranslateFn, reasonKey),
        }
      }),
    [allowedTerms, applyValidation, validTerms, invalidTerms, t],
  )

  return (
    <ControlledSelect
      name={'term' as FieldPathByValue<T, number>}
      control={control}
      label={fieldLabel}
      options={options}
      parseValue={(v) => (v !== null && v !== '' ? Number(v) : null)}
      disabled={disabled || isLockedByPromoPlan}
      disableEmptyOption
      fullWidth
    />
  )
}

const TermActive = memo(TermActiveImpl) as typeof TermActiveImpl

interface PaymentFrequencyProps<T extends FieldValues & { paymentFrequency: EPaymentFrequency }> {
  control: Control<T>
  locked?: boolean
  label?: string
}

const PaymentFrequency = <T extends FieldValues & { paymentFrequency: EPaymentFrequency }>({
  control,
  locked,
  label,
}: PaymentFrequencyProps<T>) => {
  const { t } = useTranslation()
  const { supportedPaymentFrequencies, isSinglePayment } = useBaseWorksheetFormConfig()
  const frequencies = useMemo(
    () => (isSinglePayment ? [EPaymentFrequency.Single] : supportedPaymentFrequencies),
    [isSinglePayment, supportedPaymentFrequencies],
  )
  const options = useTranslatedOptionEnum('EPaymentFrequency', frequencies, 'enum')
  const isLocked = locked ?? frequencies.length <= 1

  const fieldName = 'paymentFrequency' as FieldPathByValue<T, EPaymentFrequency>
  const { field } = useController({ control, name: fieldName })

  useEffect(() => {
    if (isSinglePayment && field.value !== EPaymentFrequency.Single) {
      field.onChange(EPaymentFrequency.Single)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSinglePayment])

  return (
    <Grid size={cellSize}>
      <ControlledSelect
        name={fieldName}
        control={control}
        label={label ?? t('worksheetCommon.paymentFrequency')}
        options={options}
        disabled={isLocked}
        disableEmptyOption
        fullWidth
      />
    </Grid>
  )
}

interface ActivationDateProps<T extends FieldValues & { activationDate: DateOnly }> {
  control: Control<T>
  maxDate?: DateOnly
  label?: string
}

const ActivationDate = <T extends FieldValues & { activationDate: DateOnly }>({
  control,
  maxDate,
  label,
}: ActivationDateProps<T>) => {
  const { t } = useTranslation()
  const { editLocked } = useBaseWorksheetFormConfig()
  return (
    <Grid size={cellSize}>
      <ControlledDateOnlyPicker
        name={'activationDate' as FieldPathByValue<T, DateOnly>}
        control={control}
        label={label ?? t('worksheetCommon.activationDate')}
        disablePast
        maxDate={maxDate}
        disabled={!!editLocked}
        fullWidth
      />
    </Grid>
  )
}

type FirstPaymentDateConstraint = FieldValues & {
  firstPaymentDate: DateOnly | null
  activationDate: DateOnly
  paymentFrequency: EPaymentFrequency
  isHighEstimate?: boolean
}

const FIRST_PAYMENT_DATE_PREREQUISITES = [
  { name: 'activationDate', prefixKey: 'worksheetCommon' },
  { name: 'paymentFrequency', prefixKey: 'worksheetCommon' },
] as const satisfies readonly FieldPrerequisiteSpec<FirstPaymentDateConstraint, 'worksheetCommon'>[]

const FIRST_PAYMENT_DATE_WATCH = ['isHighEstimate'] as const

interface FirstPaymentDateProps<T extends FirstPaymentDateConstraint> {
  control: Control<T>
  label?: string
}

const FirstPaymentDate = <T extends FirstPaymentDateConstraint>({ control, label }: FirstPaymentDateProps<T>) => {
  const { t } = useTranslation()
  const fieldName = 'firstPaymentDate' as FieldPathByValue<T, DateOnly | null>
  const fieldLabel = label ?? t('worksheetCommon.firstPaymentDate')

  return (
    <Grid size={cellSize}>
      <FieldReadyGate
        control={control}
        requiredFields={FIRST_PAYMENT_DATE_PREREQUISITES}
        watch={FIRST_PAYMENT_DATE_WATCH}
        selfName="firstPaymentDate"
        shell={
          <ControlledSelect
            name={fieldName}
            control={control}
            label={fieldLabel}
            options={[]}
            parseValue={(v) => (v ? new DateOnly(v) : null)}
            disabled
            disableEmptyOption
            fullWidth
          />
        }
      >
        {(values) => (
          <FirstPaymentDateActive
            control={control}
            fieldName={fieldName}
            fieldLabel={fieldLabel}
            activationDate={values.activationDate}
            paymentFrequency={values.paymentFrequency}
            isHighEstimate={values.isHighEstimate}
          />
        )}
      </FieldReadyGate>
    </Grid>
  )
}

interface FirstPaymentDateActiveProps<T extends FirstPaymentDateConstraint> {
  control: Control<T>
  fieldName: FieldPathByValue<T, DateOnly | null>
  fieldLabel: string
  activationDate: DateOnly
  paymentFrequency: EPaymentFrequency
  isHighEstimate: boolean | undefined
}

const FirstPaymentDateActiveImpl = <T extends FirstPaymentDateConstraint>({
  control,
  fieldName,
  fieldLabel,
  activationDate,
  paymentFrequency,
  isHighEstimate,
}: FirstPaymentDateActiveProps<T>) => {
  const { financingProgramId, creditApplicationId } = useBaseWorksheetFormConfig()

  const optionsDto = useMemo<FirstPaymentDateOptionsDto | null>(() => {
    if (!paymentFrequency) return null
    return {
      deliveryOn: activationDate,
      systemOriginId: EOriginSystemId.Backoffice,
      isHighEstimate: isHighEstimate ?? false,
      paymentFrequency,
    }
  }, [activationDate, paymentFrequency, isHighEstimate])

  const [apiOptions, isFetching] = useGetFirstPaymentDateOptions(financingProgramId, creditApplicationId, optionsDto)
  useReportFieldLoading('firstPaymentDate', isFetching)

  const dateOptions = useMemo(() => apiOptions.map((d) => new DateOnly(d)), [apiOptions])

  const { isValid, allowedKeys } = useReconcileFieldToOptions({
    control,
    name: fieldName,
    options: dateOptions,
    getKey: (d) => d.toString(),
    enabled: dateOptions.length > 0,
  })

  const selectOptions = useMemo(
    () => renderSelectMenuItems(dateOptions, (d) => ({ value: d.toString(), label: formatDate(d) })),
    [dateOptions],
  )

  if (isFetching || dateOptions.length === 0 || !isValid) {
    return (
      <ControlledSelect
        name={fieldName}
        control={control}
        label={fieldLabel}
        options={[]}
        parseValue={(v) => (v ? new DateOnly(v) : null)}
        disabled
        loading={isFetching}
        disableEmptyOption
        fullWidth
      />
    )
  }

  const useDatePicker = dateOptions.length > FIRST_PAYMENT_DATE_PICKER_THRESHOLD

  return useDatePicker ? (
    <ControlledDateOnlyPicker
      name={fieldName}
      control={control}
      label={fieldLabel}
      minDate={dateOptions[0]}
      maxDate={dateOptions[dateOptions.length - 1]}
      shouldDisableDate={(d: Date) => !allowedKeys.has(new DateOnly(d).toString())}
      fullWidth
    />
  ) : (
    <ControlledSelect
      name={fieldName}
      control={control}
      label={fieldLabel}
      options={selectOptions}
      parseValue={(v) => (v ? new DateOnly(v) : null)}
      disableEmptyOption
      fullWidth
    />
  )
}

const FirstPaymentDateActive = memo(FirstPaymentDateActiveImpl) as typeof FirstPaymentDateActiveImpl

interface IsHighEstimateProps<T extends FieldValues & { isHighEstimate: boolean }> {
  control: Control<T>
  label?: string
}

const IsHighEstimate = <T extends FieldValues & { isHighEstimate: boolean }>({
  control,
  label,
}: IsHighEstimateProps<T>) => {
  const { t } = useTranslation()
  const { canAcceptHighEstimate, supportsHighEstimate } = useBaseWorksheetFormConfig()
  const featureFlags = useFeatureFlags()

  const fieldName = 'isHighEstimate' as FieldPathByValue<T, boolean>
  const { field } = useController({ control, name: fieldName })
  const paymentPlanId = useWatch({ control, name: 'paymentPlanId' as FieldPath<T> }) as string | undefined
  const onPromoPlan = isPromoPlan(paymentPlanId)

  useEffect(() => {
    if (!featureFlags.useHighEstimateOnBuyDown && onPromoPlan && field.value) {
      field.onChange(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.useHighEstimateOnBuyDown, onPromoPlan])

  if (!supportsHighEstimate || canAcceptHighEstimate !== true) return null

  return (
    <Grid size={cellSize}>
      <ControlledCheckbox
        name={fieldName}
        control={control}
        label={label ?? t('worksheetCommon.checkIsHighEstimate')}
      />
    </Grid>
  )
}

interface IncludeProtectionProps<T extends FieldValues & { includeProtection: boolean }> {
  control: Control<T>
  label?: string
}

const IncludeProtection = <T extends FieldValues & { includeProtection: boolean }>({
  control,
  label,
}: IncludeProtectionProps<T>) => {
  const { t } = useTranslation()
  const { supportsInsurance } = useBaseWorksheetFormConfig()

  if (!supportsInsurance) return null

  return (
    <Grid size={cellSize}>
      <ControlledCheckbox
        name={'includeProtection' as FieldPathByValue<T, boolean>}
        control={control}
        label={label ?? t('worksheetCommon.protectionPlan')}
      />
    </Grid>
  )
}

BaseWorksheetForm.AmountRequested = memo(AmountRequested) as typeof AmountRequested
BaseWorksheetForm.PaymentPlan = memo(PaymentPlan) as typeof PaymentPlan
BaseWorksheetForm.Term = memo(Term) as typeof Term
BaseWorksheetForm.PaymentFrequency = memo(PaymentFrequency) as typeof PaymentFrequency
BaseWorksheetForm.ActivationDate = memo(ActivationDate) as typeof ActivationDate
BaseWorksheetForm.FirstPaymentDate = memo(FirstPaymentDate) as typeof FirstPaymentDate
BaseWorksheetForm.IncludeProtection = memo(IncludeProtection) as typeof IncludeProtection
BaseWorksheetForm.IsHighEstimate = memo(IsHighEstimate) as typeof IsHighEstimate

export default BaseWorksheetForm
